import React, { useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare, faChevronDown, faChevronRight, faPaperclip, faPenNib,
} from '@fortawesome/pro-regular-svg-icons';

import SanitizedHTML from 'components/ui/sanitized_html';
import { AI_HTML_OPTIONS } from 'components/ai/html';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Button } from 'components/ui/button';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Collapsible } from '@buildoutinc/blueprint-react/ui/Collapsible';
import { formatBytes } from 'helpers/formatters/file_size_formats';
import { useConversationContext } from 'components/ai/assistant/conversation_context';
import { writeOpenState } from 'components/ai/assistant/sidebar/open_state';

import type { ActionVerdict, AssistantAction, Message } from 'components/ai/assistant/types';
import type { SendEmailToolCall } from '../types';

interface EmailCardsProps {
  toolCalls: SendEmailToolCall[];
}

interface EmailCardProps {
  action?: AssistantAction;
  busy: boolean;
  toolCall: SendEmailToolCall;
  onResolve: (action: AssistantAction, verdict: ActionVerdict) => void;
}

export function EmailCards({ toolCalls }: EmailCardsProps) {
  const { busy, messages, resolveAction } = useConversationContext();

  return (
    <>
      {toolCalls.map(toolCall => (
        <EmailCard
          action={findAction(messages, toolCall.metadata.actionId)}
          busy={busy}
          key={toolCall.metadata.actionId}
          toolCall={toolCall}
          onResolve={resolveAction}
        />
      ))}
    </>
  );
}
EmailCards.claimsActions = true;

function EmailCard({ action, busy, toolCall, onResolve }: EmailCardProps) {
  const { attachments, body, editPath, subject, to } = toolCall.metadata;
  const [open, setOpen] = useState(true);

  return (
    <Card>
      <Card.Body>
        <Collapsible open={open} onOpenChange={setOpen}>
          <Collapsible.Trigger className="d-flex align-items-center gap-2 w-100 p-0 border-0 bg-transparent text-start">
            <FontAwesomeIcon className="text-primary" icon={faPenNib} />
            <span className="fw-bold fs-small text-muted text-uppercase">Email draft</span>
            {!open && <span className="flex-grow-1 fw-bold text-truncate ms-2">{subject}</span>}
            <FontAwesomeIcon className="ms-auto text-muted" icon={open ? faChevronDown : faChevronRight} />
          </Collapsible.Trigger>

          <Collapsible.Content>
            <div className="d-flex flex-column gap-2 pt-2">
              <div className="fw-bold">{subject}</div>

              <div className="d-flex flex-wrap gap-1">
                {to.map(recipient => (
                  <Badge appearance="muted" key={recipient.email} variant="secondary">
                    {recipient.name} &lt;{recipient.email}&gt;
                  </Badge>
                ))}
              </div>

              <SanitizedHTML className="ai-assistant-prose fs-small" html={body} {...AI_HTML_OPTIONS} />

              {attachments && attachments.length > 0 && (
                <div className="d-flex flex-column gap-1">
                  {attachments.map(attachment => (
                    <div className="d-flex align-items-center border rounded px-2 py-1 small" key={attachment.gid}>
                      <FontAwesomeIcon className="me-2 text-muted" icon={faPaperclip} />
                      <span className="text-truncate">{attachment.filename}</span>
                      <span className="text-muted fs-xs ms-2">{formatBytes(attachment.byteSize)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Collapsible.Content>
        </Collapsible>

        {action?.status === 'pending' && (
          <div className="d-flex align-items-center gap-2 mt-2">
            <Button disabled={busy} label="Send it" size="sm" onClick={() => onResolve(action, 'apply')} />
            {editPath && (
              <Button
                href={editPath}
                iconEnd={faArrowUpRightFromSquare}
                label="Let me edit"
                size="sm"
                variant="outline"
                // The draft carries on in the form on the page this opens, so the panel is done.
                onClick={() => writeOpenState(false)}
              />
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function findAction(messages: Message[], actionId: number): AssistantAction | undefined {
  return messages.map(message => message.actions?.find(action => action.id === actionId)).find(Boolean);
}
